import { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { change, reduxForm, SubmissionError, formValueSelector, getFormAsyncErrors } from 'redux-form';
import { useTranslation } from 'react-i18next';

import { config } from 'app/config';
import { notify } from 'libs/common';
import { addRfid, updateRfid } from 'services/payments-service';
import { getCategoriesList, getVehicleClassesList, getOptionsList, isNeedAddCurrentTariff } from 'services/types';
import { Confirm, Button, themes, Popup2 } from 'libs/ui';
import { ParkingFilter2, WhiteStyledField, Title, ErrorMsg, required, isRfid, DatePeriod } from 'libs/form';
import { StyledButtonContainer, TextareaStyledField } from './rfid-form.styled';
import { asyncValidate } from './rfid-form.validate';
import { getShortRFID } from 'services/types';

/**
 * rfid form
 * @param {*} props
 * @returns
 */
const RfidForm = (props) => {
  const { initialValues, error, destroy, refresh, form, handleSubmit, close, type, clientId, tariffs, parkingId, dispatch, formAsyncErrors } = props;
  const [confirm, setConfirm] = useState(false);

  const isAdd = type === 'addForm';

  const needAddTariff = isNeedAddCurrentTariff(isAdd, initialValues?.tariff_id, tariffs);

  useEffect(() => {
    window.rfidFormEdit = !isAdd;

    return () => {
      window.rfidFormEdit = undefined;
    };
  }, [isAdd]);

  const { t } = useTranslation();
  const requiredT = useCallback(required(t), [t]);
  const rfidT = useCallback(isRfid(t), [t]);
  const needConfirm = formAsyncErrors?.confirm;

  useEffect(() => {
    if (needConfirm) {
      setConfirm(true);
    }
    return () => {
      setConfirm(false);
    };
  }, [needConfirm]);

  /**
   * close confirm
   */
  const closeConfirm = () => {
    setConfirm(false);
  };

  /**
   * after submit
   */
  const afterSubmit = () => {
    refresh();
    notify(t('messages.ВНИМАНИЕ!'), isAdd ? t('messages.Сохранено успешно') : t('messages.Изменения сохранены'), 'success');

    close();
    destroy(form);
  };

  /**
   * save
   * @param {*} values
   * @returns
   */
  const submit = (values) => {
    // data
    const test = false;
    const out = { ...values };
    // out.is_blocked = out.is_blocked === 'true' || out.is_blocked === true;
    // out.mean_type = 'subscription';
    out.vehicle_category = out.vehicle_category ? parseInt(out.vehicle_category) : null;
    out.vehicle_class = out.vehicle_class ? parseInt(out.vehicle_class) : null;
    out.tariff_id = out.tariff_id ? out.tariff_id : null;
    if (clientId) {
      out.client_id = clientId;
    }

    // if (!isAdd) {
    //   out.id = id;
    //   // confirm
    //   if (out.mean_id !== props.initialValues.mean_id) {
    //     setData(out);
    //     setConfirm(true);
    //     return false;
    //   }
    // }
    if (test) {
      console.log('🚀 ~ submit ~ out:', out);
      return Promise.resolve();
    }

    // method
    const method = isAdd && out.mode === 'normal' ? addRfid : updateRfid;
    if (out.mode !== 'normal') {
      out.id = out.id1;
    }
    delete out.id1;
    delete out.mode;
    delete out.client_id1;
    return method(out)
      .then(() => {
        afterSubmit();
      })
      .catch((errors) => {
        // console.log('🚀 ~ submit ~ errors:', errors);
        if (errors && errors.data) {
          throw new SubmissionError({
            _error: errors.data.type,
          });
        }
        if (errors && errors.field) {
          throw new SubmissionError({
            [errors.field]: errors.message,
          });
        }
      });
  };

  const changeRfid = () => {
    window.rfidFormEdit = true;
    const found = window.rfid;
    // console.log('🚀 ~ changeRfid ~ found:', found);
    // this.setState({ edit1: true });
    dispatch(change(form, 'rfid', found.rfid));
    dispatch(change(form, 'id1', found.id));
    dispatch(change(form, 'mode', 'change'));
    dispatch(change(form, 'owner', found.owner));
    dispatch(change(form, 'vehicle_plate', found.vehicle_plate));
    dispatch(change(form, 'vehicle_category', found.vehicle_category));
    dispatch(change(form, 'vehicle_class', found.vehicle_class));
    dispatch(change(form, 'tariff_id', found.tariff_id));
    closeConfirm();
  };

  /**
   * close form
   */
  const cancel = () => {
    close();
    destroy(form);
  };

  const title = isAdd ? t('form.Добавить RFID') : t('form.Изменить RFID');
  const disabledRfid = window.config.central && !parkingId;

  return (
    <Popup2 w={482} top={3}>
      <Title>{title}</Title>
      <form onSubmit={handleSubmit(submit)}>
        <ErrorMsg error={error} />

        <input type="hidden" name="mode" />
        <input type="hidden" name="id1" />
        <input type="hidden" name="client_id1" />
        {isAdd && <ParkingFilter2 withEmpty required />}

        <WhiteStyledField
          validate={[requiredT, rfidT]}
          placeholder="78497503480098"
          // mask="999999"
          error={error}
          type="text"
          label={t('sections.RFID') + config.req}
          name={isAdd ? 'rfid' : 'mean_id'}
          disabled={disabledRfid}
        />

        <WhiteStyledField validate={requiredT} placeholder={t('form.Иванов Иван Иванович')} type="text" error={error} label={t('form.ФИО водителя') + config.req} name="owner" />

        <WhiteStyledField validate={requiredT} type="select" label={t('form.Класс ТС') + config.req} error={error} name="vehicle_class">
          {isAdd && <option value={''}>{t('form.Выберите класс ТС')}</option>}
          {getVehicleClassesList()}
        </WhiteStyledField>

        <WhiteStyledField validate={requiredT} type="select" label={t('filter.Категория СО') + config.req} error={error} name="vehicle_category">
          {isAdd && <option value={''}>{t('form.Выберите категорию')}</option>}
          {getCategoriesList()}
        </WhiteStyledField>

        <DatePeriod />

        <WhiteStyledField validate={requiredT} placeholder={t('form.А000AA 000 rus')} type="text" label={t('sections.ГРЗ') + config.req} error={error} name="vehicle_plate" />

        <WhiteStyledField
          className="shortfield"
          type="select"
          label={t('form.Тариф') + config.req}
          name="tariff_id"
          error={error}
          // disabled={!transportClass || !transportCategory}
          validate={requiredT}
        >
          {isAdd && <option value="">{t('form.Выберите тариф')}</option>}
          {needAddTariff && <option value={initialValues?.tariff_id}>{initialValues?.tariff} (а)</option>}
          {getOptionsList(tariffs)}
        </WhiteStyledField>

        {isAdd && <TextareaStyledField type="textarea" placeholder={t('filter.Напишите свой комментарий')} label={t('filter.Комментарий')} name="comment" />}

        <StyledButtonContainer space="20px">
          <Button type="button" onClick={cancel}>
            {t('buttons.Отменить')}
          </Button>
          <Button theme={themes.blue}>
            <i className="fa fa-save" />
            {t('buttons.Сохранить')}
          </Button>
        </StyledButtonContainer>

        {/* {confirm && (
          <Confirm
            width={'550px'}
            title={'Номер карты для данной записи будет изменён. Предыдущий номер карты будет удалён. Продолжить?'}
            type="danger"
            cancelButton={{ text: 'Отмена', func: closeConfirm }}
            acceptButton={{ text: 'Изменить номер карты', func: submitAfterConfirm, theme: themes.red }}
          />
        )} */}
        {confirm && (
          <Confirm
            w={480}
            title={t('messages.Метка с таким номером привязана к другому клиенту Вы уверены, что хотите добавить метку к текущему клиенту?')}
            type="danger"
            cancelButton={{ text: t('buttons.Отменить'), func: closeConfirm }}
            acceptButton={{ text: t('grid.Подтвердить'), func: changeRfid, theme: themes.blue }}
          />
        )}
      </form>
    </Popup2>
  );
};

//vehicle_category: props.categories ? props.categories[0].id : null,

let reduxRfidForm = reduxForm({
  form: 'rfidForm',
  asyncValidate,
  asyncBlurFields: ['rfid'],
  // destroyOnUnmount: true,
})(RfidForm);

const selector = formValueSelector('rfidForm');

reduxRfidForm = connect((state, props) => {
  // const transportClass = selector(state, 'vehicle_class');
  // const transportCategory = selector(state, 'vehicle_category');
  const parkingId = selector(state, 'parking_id');
  // const rfid = selector(state, 'rfid');
  const out = { ...props.initialValues, mode: 'normal', client_id1: props.clientId, id1: props.initialValues.id };
  if (!out.rfid && out.mean_id) {
    out.mean_id = getShortRFID(out.mean_id);
  }
  // console.log("out", out);
  return {
    // transportClass,
    // transportCategory,
    // rfid,
    parkingId,
    formAsyncErrors: getFormAsyncErrors('rfidForm')(state),
    initialValues: out,
  };
}, {})(reduxRfidForm);

export default reduxRfidForm;
